import { ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';

import { InstitutionService } from '@services/institution.service';
import { LoadingService } from '@services/loading.service';
import { ReportService } from '@services/report.service';
import { PdfExportService, PdfColumn } from '@services/pdf-export.service';
import { LoggerService } from '@services/logger.service';

import { SelectOption } from '@interfaces/input-select-option.interface';
import { ReportClinicPurchasesItem } from '@interfaces/report';

import { UserAction } from '@enums/user-action';

import moment from 'moment';
import { CallsService } from '@services/api/calls.service';
import { Utils } from '@json/src/app/Utils';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-report-purchases-hospital',
  templateUrl: './report-purchases-hospital.component.html'
})
export class ReportPurchasesHospitalComponent implements OnInit, OnDestroy {

  @ViewChild(MatSort) sort: MatSort;

  public form: FormGroup;

  public selectOptionsInsitutions: SelectOption[];
  public selectOptionsProveedores: SelectOption[] = [];

  readonly minDate = new Date(moment().year(), moment().month(), moment().date());
  maxDateFrom: Date;
  minDateTo = new Date(moment().year(), moment().month(), moment().date());

  ELEMENT_DATA: ReportClinicPurchasesItem[] = [];

  displayedColumns: string[] = ['DrugId', 'NombreMedicamento', 'PVLN', 'Unidades', 'Quantity', 'Stock', 'Poveedor', 'Cajas'];
  dataSource = new MatTableDataSource<ReportClinicPurchasesItem>(this.ELEMENT_DATA);

  dataRequested: boolean = false;

  private subs: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private institutionService: InstitutionService,
    private loadingService: LoadingService,
    private reportService: ReportService,
    private cdRef: ChangeDetectorRef,
    private calls: CallsService,
    private pdfExportService: PdfExportService,
    private logger: LoggerService
  ) {
    this.form = this.formBuilder.group({
      selectedInstitution: ['', Validators.required],
      fromDate: [moment(), Validators.required],
      toDate: [moment(), Validators.required],
      filter: [''],
      selectedProveedor: ['']
    });

    this.selectOptionsInsitutions = [];
  }

  ngOnInit(): void {
    this.loadingService.start('Cargando datos...');

    // Inicializar maxDateFrom con la fecha actual del formulario
    this.maxDateFrom = this.form.controls['toDate'].value.toDate();

    this.institutionService.getInstitutions().then(() => {
      this.selectOptionsInsitutions = Object.assign([], this.institutionService.institutionList.getValue());
      this.selectOptionsInsitutions = this.selectOptionsInsitutions.sort((a, b) => a.label.localeCompare(b.label))

      if (this.selectOptionsInsitutions.length === 1) {
        this.form.controls['selectedInstitution'].setValue(this.selectOptionsInsitutions[0].value);
        this.form.controls['selectedInstitution'].disable();
      }

      this.loadingService.stop();
    });

    // Configurar filterPredicate personalizado
    this.dataSource.filterPredicate = (data: ReportClinicPurchasesItem, filter: string) => {
      const filterObj = JSON.parse(filter);
      const searchStr = filterObj.search?.toLowerCase() || '';
      const proveedor = filterObj.proveedor || '';

      // Filtrar por texto de búsqueda
      const matchesSearch = !searchStr ||
        data.DrugId?.toLowerCase().includes(searchStr) ||
        data.NombreMedicamento?.toLowerCase().includes(searchStr) ||
        data.Poveedor?.toLowerCase().includes(searchStr);

      // Filtrar por proveedor
      const matchesProveedor = !proveedor || data.Poveedor === proveedor;

      return matchesSearch && matchesProveedor;
    };

    this.subs.push(
      this.form.controls['filter'].valueChanges.subscribe((value) => {
        this.applyFilter();
      }),
      this.form.controls['selectedProveedor'].valueChanges.subscribe((value) => {
        this.applyFilter();
      }),
      // Actualizar maxDateFrom cuando cambie toDate
      this.form.controls['toDate'].valueChanges.subscribe((value) => {
        if (value) {
          this.maxDateFrom = value.toDate();
          // Si fromDate es posterior a toDate, ajustar fromDate
          const fromDate = this.form.controls['fromDate'].value;
          if (fromDate && fromDate.isAfter(value)) {
            this.form.controls['fromDate'].setValue(value);
          }
        }
      }),
      // Actualizar minDateTo cuando cambie fromDate
      this.form.controls['fromDate'].valueChanges.subscribe((value) => {
        if (value) {
          this.minDateTo = value.toDate();
          // Si toDate es anterior a fromDate, ajustar toDate
          const toDate = this.form.controls['toDate'].value;
          if (toDate && toDate.isBefore(value)) {
            this.form.controls['toDate'].setValue(value);
          }
        }
      })
    );

    this.logger.logPage('Reporte: Compras Clinic');
  }

  applyFilter(): void {
    const filterValue = {
      search: this.form.controls['filter'].value || '',
      proveedor: this.form.controls['selectedProveedor'].value || ''
    };
    this.dataSource.filter = JSON.stringify(filterValue);
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  handleGenerateReport(): void {
    const filteredData = this.dataSource.filteredData;
    if (filteredData.length === 0) {
      console.warn('No hay datos disponibles para exportar');
      return;
    }

    this.generatePDF();
  }

  handleGenerateExcel(): void {
    const filteredData = this.dataSource.filteredData;
    if (filteredData.length === 0) {
      console.warn('No hay datos disponibles para exportar');
      return;
    }

    this.generateExcel();
  }

  generatePDF(): void {
    const institutionName = this.form.controls['selectedInstitution'].value?.name || '';
    const fromDate = this.form.controls['fromDate'].value.format('DD/MM/YYYY');
    const toDate = this.form.controls['toDate'].value.format('DD/MM/YYYY');
    const selectedProveedor = this.form.controls['selectedProveedor'].value;

    // Definir las columnas del PDF
    const columns: PdfColumn[] = [
      // { header: 'Centro', field: 'InstitutionId', width: 'auto', align: 'center' },
      { header: 'CN', field: 'DrugId', width: 'auto', align: 'left', bold: true },
      { header: 'Medicamento', field: 'NombreMedicamento', width: '*', align: 'left', bold: true },
      {
        header: 'PVLN',
        field: 'PVLN',
        width: 'auto',
        align: 'right',
        format: (value) => value != null ? value.toFixed(2) + '€' : '-'
      },
      { header: 'Unidades', field: 'Unidades', width: 'auto', align: 'center' },
      { header: 'Cantidad', field: 'Quantity', width: 'auto', align: 'center' },
      { header: 'Stock', field: 'Stock', width: 'auto', align: 'center' }
    ];

    // Solo añadir columna de proveedor si no hay filtro de proveedor seleccionado
    if (!selectedProveedor) {
      columns.push({ header: 'Proveedor', field: 'Poveedor', width: '*', align: 'left' });
    }

    columns.push({ header: 'Cajas', field: 'Cajas', width: 'auto', align: 'center' });

    // Usar datos filtrados
    const filteredData = this.dataSource.filteredData;

    // Preparar metadatos
    const metadata: { label: string; value: string }[] = [
      { label: 'Fecha desde', value: fromDate },
      { label: 'Fecha hasta', value: toDate }
    ];

    // Añadir proveedor si está seleccionado
    if (selectedProveedor) {
      metadata.push({ label: 'Proveedor', value: selectedProveedor });
    }

    metadata.push({ label: 'Total registros', value: filteredData.length.toString() });

    // Configuración del PDF
    this.pdfExportService.exportToPdf({
      title: 'Reporte de Compras',
      subtitle: institutionName,
      filename: `reporte-compras-${institutionName}-${moment().format('YYYY-MM-DD')}.pdf`,
      orientation: 'portrait', // 'landscape' para apaisado, 'portrait' para vertical
      pageSize: 'A4', // 'A4', 'A3', 'LETTER'
      columns: columns,
      data: filteredData,
      metadata: metadata,
      // footer: `Generado el ${moment().format('DD/MM/YYYY HH:mm')}`
    });

    this.logger.logAction(UserAction.Click, {
      message: 'Exportar PDF',
      file: `reporte-compras-${institutionName}-${moment().format('YYYY-MM-DD')}.pdf`,
      records: this.dataSource.data.length
    });
  }

  generateExcel(): void {
    // Usar datos filtrados
    const filteredData = this.dataSource.filteredData;

    // Preparar los datos para Excel con nombres de columnas personalizados
    const excelData: any[] = [];

    // Agregar datos con las columnas renombradas
    filteredData.forEach(row => {
      const rowData: any = {
        // 'InstitutionId': row.InstitutionId !== undefined && row.InstitutionId !== null ? row.InstitutionId : '',
        'CN': row.DrugId !== undefined && row.DrugId !== null ? row.DrugId : '',
        'Medicamento': row.NombreMedicamento !== undefined && row.NombreMedicamento !== null ? row.NombreMedicamento : '',
        'PVLN': row.PVLN !== undefined && row.PVLN !== null ? row.PVLN : '',
        'Unidades': row.Unidades !== undefined && row.Unidades !== null ? row.Unidades : '',
        'Cantidad': row.Quantity !== undefined && row.Quantity !== null ? row.Quantity : '',
        'Stock': row.Stock !== undefined && row.Stock !== null ? row.Stock : '',
        'Poveedor': row.Poveedor !== undefined && row.Poveedor !== null ? row.Poveedor : '',
        'Cajas': row.Cajas !== undefined && row.Cajas !== null ? row.Cajas : ''
      };
      excelData.push(rowData);
    });

    // Crear el libro de trabajo (workbook)
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Reporte Compras': worksheet },
      SheetNames: ['Reporte Compras']
    };

    // Generar nombre del archivo con fecha actual
    const fromDate = this.form.controls['fromDate'].value.format('YYYY-MM-DD');
    const toDate = this.form.controls['toDate'].value.format('YYYY-MM-DD');
    const institutionName = this.form.controls['selectedInstitution'].value?.shortName || 'institucion';
    const filename = `reporte-compras-${institutionName}-${fromDate}_${toDate}.xlsx`;

    this.logger.logAction(UserAction.Click, {
      message: 'Exportar Excel',
      file: `reporte-compras-${institutionName}-${fromDate}_${toDate}.xlsx`,
      records: this.dataSource.data.length
    });

    // Exportar el archivo
    XLSX.writeFile(workbook, filename);
  }

  generateCSV(): void {
    // Definir las cabeceras del CSV
    const headers = [
      'InstitutionId',
      'DrugId',
      'NombreMedicamento',
      'PVLN',
      'Unidades',
      'Quantity',
      'Stock',
      'Poveedor',
      'Cajas'
    ];

    // Generar el contenido CSV
    let csvContent = headers.join(';') + '\n';

    this.dataSource.data.forEach(row => {
      const csvRow = headers.map(field => {
        const value = row[field as keyof ReportClinicPurchasesItem];
        // Escapar comillas y envolver en comillas si contiene comas o comillas
        if (value !== undefined && value !== null) {
          const stringValue = value.toString();
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return '"' + stringValue.replace(/"/g, '""') + '"';
          }
          return stringValue;
        }
        return '';
      });
      csvContent += csvRow.join(';') + '\n';
    });

    this.downloadCSV(csvContent);
  }

  downloadCSV(csvContent: string): void {
    // Crear un blob con el contenido CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Crear un enlace temporal para la descarga
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      // Generar nombre del archivo con fecha actual
      const today = moment().format('MM-YYYY');
      const institutionName = this.form.controls['selectedInstitution'].value?.shortName || 'institucion';
      const filename = `reporte-facturacion-${institutionName}-${today}.txt`;
      
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL
      URL.revokeObjectURL(url);
    }
  }

  requestData(): void {
    this.loadingService.start('Obteniendo reporte...');

    this.logger.logAction(UserAction.Click, {
      message: 'Obtener datos',
      ...{
        institutionList: this.form.controls['selectedInstitution'].value.id.toString(),
        dateFrom: Utils.BUG_FixDateForTimeZone(this.form.controls['fromDate'].value.toDate())?.toISOString().split(".")[0],
        dateUntil: Utils.BUG_FixDateForTimeZone(this.form.controls['toDate'].value.toDate())?.toISOString().split(".")[0]
      },
    });

    this.reportService.getReportData({
      report: 'ClinicCompras',
      params: {
        institutionList: this.form.controls['selectedInstitution'].value.id.toString(),
        dateFrom: Utils.BUG_FixDateForTimeZone(this.form.controls['fromDate'].value.toDate())?.toISOString().split(".")[0],
        dateUntil: Utils.BUG_FixDateForTimeZone(this.form.controls['toDate'].value.toDate())?.toISOString().split(".")[0]
      },
    }).subscribe({
      next: (response: any) => {
        this.dataSource.data = response;
        this.dataRequested = true;

        // Extraer proveedores únicos de los datos
        const proveedoresSet = new Set<string>(
          response
            .map((item: ReportClinicPurchasesItem) => item.Poveedor)
            .filter((p: string | undefined) => p && p.trim() !== '') as string[]
        );
        const proveedoresUnicos: string[] = Array.from(proveedoresSet).sort();

        this.selectOptionsProveedores = [
          { value: '', label: 'Todos los proveedores' },
          ...proveedoresUnicos.map((proveedor) => ({
            value: proveedor,
            label: proveedor
          }))
        ];

        this.cdRef.detectChanges();
        this.dataSource.sort = this.sort;
      },
      error: (error) => {
        this.loadingService.stop();
        this.calls.openSnack(error.error.payload.ErrorMessage);
      },
      complete: () => {
        this.loadingService.stop();
      }
    });
  }
}
